import React, { createContext, type ReactNode, useContext, useMemo, useState } from 'react'

const MergeGridContext = createContext<MergeGridValue | null>(null)

export const useMergeGridContext = (): MergeGridValue => {
  const context = useContext(MergeGridContext)
  if (!context) throw new Error('useMergeGridContext needs MergeGridProvider as a parent.')
  return context
}

/** The grid context, or null outside a MergeGridProvider (a column heading rendered on its own) */
export const useOptionalMergeGridContext = (): MergeGridValue | null => useContext(MergeGridContext)

/** The column shown beside the Main person when the grid is too narrow for all three */
export type CompanionColumn = 'duplicate' | 'resultant'

type MergeGridValue = {
  showThirdColumn: boolean
  /** Set only when switchColumns: which column is shown beside the Main person */
  companionColumn?: CompanionColumn
  setCompanionColumn: (column: CompanionColumn) => void
}

type MergeGridProviderProps = {
  children: ReactNode
  showThirdColumn: boolean
  /** Show two of the three columns at a time, the Main person and a switchable companion (CompanionColumnSwitch) */
  switchColumns?: boolean
}

export default function MergeGridProvider({
  children,
  showThirdColumn,
  switchColumns = false,
}: MergeGridProviderProps): JSX.Element {
  const [companionColumn, setCompanionColumn] = useState<CompanionColumn>('duplicate')

  const value: MergeGridValue = useMemo(
    () => ({
      companionColumn: switchColumns ? companionColumn : undefined,
      setCompanionColumn,
      showThirdColumn,
    }),
    [companionColumn, showThirdColumn, switchColumns]
  )

  return <MergeGridContext.Provider value={value}>{children}</MergeGridContext.Provider>
}
